import { AlertCircle } from 'lucide-react'
import { Banner } from '@/components/Banner'
import { InfoTooltip } from '@/components/InfoTooltip'
import { Select } from '@/components/Select'
import { BaselinePromptEditor } from '@/features/prompts/components/BaselinePromptEditor'
import type { ExtractionConfig, ExtractorsFile } from '@/types/bundle'

export interface ExtractionSectionProps {
  extraction?: ExtractionConfig
  extractors: ExtractorsFile['extractors']
  onChange: (extraction: ExtractionConfig) => void
  /** The selected extractor's own default prompt text, fetched by the parent
   * screen (PRD 00: shared components don't fetch their own data). */
  extractorDefaultPrompt: { content?: string; isLoading: boolean; isError: boolean }
  baselinePrompt: string
  onBaselinePromptChange: (value: string) => void
}

// Options are restricted to real, enabled extractors from the fetched
// extractors.json (filtered exactly the way extraction_engine.py's own
// _resolve_extractor validates them: enabled && type ===
// "bedrock_vision_extractor") - proactively prevents an invalid choice
// rather than validating after the fact, since the GUI backend doesn't check
// extractor_id existence at draft-save time at all (only real request
// runtime does). Confirmed with the user: an unselected extractor warns but
// never blocks Save.
export function ExtractionSection({
  extraction,
  extractors,
  onChange,
  extractorDefaultPrompt,
  baselinePrompt,
  onBaselinePromptChange,
}: ExtractionSectionProps) {
  const options = Object.entries(extractors)
    .filter(([, extractor]) => extractor.enabled && extractor.type === 'bedrock_vision_extractor')
    .map(([id, extractor]) => ({ value: id, label: `${id} (${extractor.model_id})` }))

  return (
    <section className="flex flex-col gap-4 rounded-control border border-border bg-surface p-4">
      <h2 className="flex items-center gap-1 text-header font-semibold text-text-primary">
        Extraction
        <InfoTooltip text="Which AI model reads the uploaded documents and pulls out the raw field values, before any lookup or output-resolution rules run." />
      </h2>

      <div className="flex flex-col gap-1">
        <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor="extractor-id">
          Extractor
          <InfoTooltip text="Which AI model configuration reads the uploaded documents for this product." />
        </label>
        <Select
          id="extractor-id"
          value={extraction?.extractor_id ?? ''}
          onChange={(extractor_id) => onChange({ ...extraction, extractor_id })}
          options={[{ value: '', label: 'Select an extractor…' }, ...options]}
          className="w-72"
        />
      </div>

      {!extraction?.extractor_id && (
        <Banner variant="warning" icon={AlertCircle} title="No extractor selected">
          A product with no extractor fails every real request at the extract_fields step. This
          isn't checked when saving the draft, only at real request time - fix it before publishing.
        </Banner>
      )}

      {/* Directly below the extractor dropdown, per Design PRD 10 - the
          Extractor-default reference block, then the Baseline Prompt
          textarea that overrides it for this product only. Shared with the
          Prompts screen's own product row (src/features/prompts/) - same
          component, two editing surfaces over the same underlying data. */}
      <BaselinePromptEditor
        extractorDefaultPrompt={extractorDefaultPrompt}
        baselinePrompt={baselinePrompt}
        onBaselinePromptChange={onBaselinePromptChange}
      />

      <div className="flex flex-col gap-1">
        <label className="flex items-center gap-1 text-meta font-medium text-text-secondary" htmlFor="extractor-model-id">
          Model override <span className="text-text-disabled">(optional)</span>
          <InfoTooltip text="Use a different AI model ID than the extractor's own default, for this product only. Leave blank to use the extractor's default model." />
        </label>
        <input
          id="extractor-model-id"
          type="text"
          value={extraction?.model_id ?? ''}
          onChange={(event) =>
            onChange({ ...(extraction ?? { extractor_id: '' }), model_id: event.target.value || undefined })
          }
          placeholder="Defaults to the extractor's own model_id"
          className="w-72 rounded-control border border-border bg-bg px-3 py-2 text-body text-text-primary outline-none placeholder:text-text-disabled"
        />
      </div>
    </section>
  )
}
